import React, { useContext, useState } from 'react';
import { Alert, Box, Button, ColumnLayout, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { PeriodType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDef, PERIOD_OPTIONS } from 'src/components/segment-cdt/create/segmentCreateUtils';
import SegmentGlPullDataSource from 'src/components/segment-cdt/create/SegmentGlPullDataSource';
import CurrencyAutosuggest from 'src/components/segment-cdt/create/CurrencyAutosuggest';

/**
 * "GL Balance Pull" column config. Captures what the contract stores for a PULL_GL column: displayName +
 * currency (dataType is fixed NUMBER) and the pullCriteria {ledgerType, periodType, dataKeys}. The ledger
 * and the per-COA-dimension multi-value data keys (with custom rollups) come from the reused
 * {@link SegmentGlPullDataSource}; this form owns name / period / currency.
 */
export default function GlPullColumnForm({ hideCancel = false }: { hideCancel?: boolean }) {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const draft = editor?.draft ?? {};
    const pc = draft.pullCriteria ?? {};
    const [localError, setLocalError] = useState('');

    // getCurrencyCodeLOV only fetches when its query is non-empty and ignores it server-side (returns the
    // full list), so pass a stable non-empty value to load the codes once; the Autosuggest filters them
    // client-side as the user types. Matches the CLI RecordsView pattern.
    const [currencyOptions] = services.customDataTablesService.getCurrencyCodeLOV('load');

    const setDraft = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload(patch));
    const setCriteria = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload({ pullCriteria: patch }));

    const onAdd = () => {
        // Duplicate/reference checks span the union of the table's existing columns (empty in the create
        // wizard, populated when adding columns to an existing table) and the queued columns.
        const union = [...(state.detail.definition?.columns ?? []), ...cw.columns];
        const others = union.map((c) => c.displayName || '');
        const otherIds = union.map((c) => c.columnId).filter((id): id is string => !!id);
        // Only enforce currency-membership once the LOV has loaded; an empty list means "cannot check".
        const validCurrencies = (currencyOptions ?? []).length ? [CONSTANTS.STAT_CURRENCY, ...(currencyOptions ?? [])] : [];
        const { column, error } = buildColumnDef(draft, others, otherIds, validCurrencies, union);
        if (error || !column) {
            setLocalError(error || 'Invalid column.');
            return;
        }
        dispatch(ACTIONS.COMMIT_COLUMN.withPayload(column));
    };

    return (
        <SpaceBetween size="l">
            <FormField label="Column name" description="Enter a unique name for this column.">
                <Input
                    value={draft.displayName ?? ''}
                    placeholder="e.g. 2D Revenue"
                    onChange={({ detail }) => setDraft({ displayName: detail.value })}
                />
            </FormField>

            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <img src={CONSTANTS.IMAGE_PATH + 'segment-gl-pull.svg'} width={28} height={28} alt="" style={{ display: 'block' }} />
                <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>Configure data source: GL Balance Pull</Box>
            </SpaceBetween>

            <ColumnLayout columns={2}>
                <FormField label="Period">
                    <Select
                        selectedOption={PERIOD_OPTIONS.find((o) => o.value === pc.periodType) ?? null}
                        options={PERIOD_OPTIONS}
                        placeholder="Select a period"
                        onChange={({ detail }) => setCriteria({ periodType: detail.selectedOption.value as PeriodType })}
                    />
                </FormField>
                <FormField label="Currency">
                    <CurrencyAutosuggest
                        options={currencyOptions ?? []}
                        value={draft.currency ?? ''}
                        onChange={(v) => setDraft({ currency: v })}
                    />
                </FormField>
            </ColumnLayout>

            <SegmentGlPullDataSource value={pc} onChange={(patch) => setCriteria(patch)} />

            {localError && <Alert type="error">{localError}</Alert>}

            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    {!hideCancel && <Button onClick={() => dispatch(ACTIONS.CANCEL_COLUMN_EDITOR)}>Cancel</Button>}
                    <Button variant="primary" onClick={onAdd}>
                        Add column
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
